import type { RideSummary, RideTrackPoint } from '@motogo/shared';

import { getAll, getOne, openDb, runTx, STORE_RIDE_POINTS, STORE_RIDES } from './db';

// Local-first ride store (IndexedDB). A recorded ride is written here first so a crash /
// refresh / lost connection can't lose it; it's uploaded when possible and marked synced.
// The list merges these with server rides (server wins by id). See CLAUDE.md → Ride Review.
// The database itself (name, version, stores) is owned by lib/db.ts.
const RIDES = STORE_RIDES;
const POINTS = STORE_RIDE_POINTS;

interface LocalRideRecord extends RideSummary {
  synced: boolean;
}

interface PointsRecord {
  id: string;
  points: RideTrackPoint[];
}

/** All locally-stored ride summaries (synced and not). Never throws — a store hiccup must
 *  not break the list; returns [] instead. */
export async function getLocalRides(): Promise<RideSummary[]> {
  try {
    if (typeof indexedDB === 'undefined') return [];
    const db = await openDb();
    try {
      return await getAll<RideSummary>(db, RIDES);
    } finally {
      db.close();
    }
  } catch {
    return [];
  }
}

/** One locally-stored ride's summary + track, or null. Lets a just-recorded ride be
 *  reviewed/replayed before it uploads (and reviewed offline). Never throws. */
export async function getLocalRideDetail(
  id: string,
): Promise<{ summary: RideSummary; track: RideTrackPoint[] } | null> {
  try {
    if (typeof indexedDB === 'undefined') return null;
    const db = await openDb();
    try {
      const rec = await getOne<LocalRideRecord>(db, RIDES, id);
      if (!rec) return null;
      const pts = await getOne<PointsRecord>(db, POINTS, id);
      return { summary: rec, track: pts?.points ?? [] };
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

/** Persists a ride (summary + track) locally. `synced` records whether it's already on the server. */
export async function saveLocalRide(summary: RideSummary, points: RideTrackPoint[], synced: boolean): Promise<void> {
  const db = await openDb();
  try {
    await runTx(db, [RIDES, POINTS], 'readwrite', (tx) => {
      tx.objectStore(RIDES).put({ ...summary, synced } satisfies LocalRideRecord);
      tx.objectStore(POINTS).put({ id: summary.id, points } satisfies PointsRecord);
    });
  } finally {
    db.close();
  }
}

/** The not-yet-uploaded rides with their tracks, for the sync pass. */
export async function getUnsyncedRides(): Promise<{ summary: RideSummary; points: RideTrackPoint[] }[]> {
  try {
    if (typeof indexedDB === 'undefined') return [];
    const db = await openDb();
    try {
      const records = await getAll<LocalRideRecord>(db, RIDES);
      const result: { summary: RideSummary; points: RideTrackPoint[] }[] = [];
      for (const rec of records) {
        if (rec.synced) continue;
        const pts = await getOne<PointsRecord>(db, POINTS, rec.id);
        result.push({ summary: rec, points: pts?.points ?? [] });
      }
      return result;
    } finally {
      db.close();
    }
  } catch {
    return [];
  }
}

/** Removes a locally-stored ride (summary + track) — used to discard non-rides. */
export async function deleteLocalRide(id: string): Promise<void> {
  const db = await openDb();
  try {
    await runTx(db, [RIDES, POINTS], 'readwrite', (tx) => {
      tx.objectStore(RIDES).delete(id);
      tx.objectStore(POINTS).delete(id);
    });
  } finally {
    db.close();
  }
}

/** Flags a locally-stored ride as uploaded. */
export async function markSynced(id: string): Promise<void> {
  const db = await openDb();
  try {
    await runTx(db, RIDES, 'readwrite', (tx) => {
      const store = tx.objectStore(RIDES);
      const get = store.get(id);
      get.onsuccess = () => {
        const rec = get.result as LocalRideRecord | undefined;
        if (rec) {
          rec.synced = true;
          store.put(rec);
        }
      };
    });
  } finally {
    db.close();
  }
}

